import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import RecognitionPublic from "./pages/RecognitionPublic";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Patients from "./pages/Patients";
import Agenda from "./pages/Agenda";
import PatientDetail from "./pages/PatientDetail";
import DoctorProfile from "./pages/DoctorProfile";
import Team from "./pages/Team";
import Statistics from "./pages/Statistics";
import Odontogram from "./pages/Odontogram";
import SharePrescription from "./pages/SharePrescription";
import PreRegistro from "./pages/PreRegistro";
import DelegateApp from "./pages/DelegateApp";
import AdminPanel from "./pages/AdminPanel";
import LicenseExpired from "./pages/LicenseExpired";
import PaymentReturn from "./pages/PaymentReturn";
import PaymentTransfer from "./pages/PaymentTransfer";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import DoctorDirectory from "./pages/DoctorDirectory";
import Bitacora from "./pages/Bitacora";
import AppLayout from "./components/AppLayout";
import PdfViewerModal from "./components/PdfViewerModal";
import TransferModal from "./components/TransferModal";
import ErrorBoundary from "./components/ErrorBoundary";
import { licenseActive } from "./lib/license";

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 rounded-full border-4 border-brand-sage/30 border-t-brand-terracotta animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/" replace />;
  if (!licenseActive(user)) return <LicenseExpired />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/reconocimiento/:id" element={<RecognitionPublic />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/receta/:shareId" element={<SharePrescription />} />
      <Route path="/pre-registro/:token" element={<PreRegistro />} />
      <Route path="/asistente/:token" element={<DelegateApp role="asistente" />} />
      <Route path="/enfermeria/:token" element={<DelegateApp role="enfermeria" />} />
      <Route path="/pago" element={<PaymentReturn />} />
      <Route path="/pago/transferencia" element={<PaymentTransfer />} />
      <Route path="/privacidad" element={<PrivacyPolicy />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/terminos" element={<TermsOfService />} />
      <Route path="/directorio" element={<DoctorDirectory />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/pacientes" element={<Protected><Patients /></Protected>} />
      <Route path="/agenda" element={<Protected><Agenda /></Protected>} />
      <Route path="/pacientes/:patientId" element={<Protected><PatientDetail /></Protected>} />
      <Route path="/perfil" element={<Protected><DoctorProfile /></Protected>} />
      <Route path="/equipo" element={<Protected><Team /></Protected>} />
      <Route path="/estadisticas" element={<Protected><Statistics /></Protected>} />
      <Route path="/bitacora" element={<Protected><Bitacora /></Protected>} />
      <Route path="/odontograma" element={<Protected><Odontogram /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><AdminPanel /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App font-nunito">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <PdfViewerModal />
            <TransferModal />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
